import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Cell,
} from "recharts";
import Card from "../../../Component/RetailerComponents/Card";
import { money } from "../../../utils/helpers";
import HeatTile from "../../../Component/RetailerComponents/HeatTile";
import { Chip } from "../../../Component/RetailerComponents/Chip";

/* ---------- reusable empty state ---------- */
function EmptyState({ text }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-slate-500">
      {text}
    </div>
  );
}

/* ---------- skeletons ---------- */

function BarChartSkeleton() {
  return (
    <div className="h-full w-full animate-pulse space-y-3">
      {/* title lines */}
      <div className="h-3 w-1/3 rounded bg-slate-200" />
      <div className="h-3 w-1/4 rounded bg-slate-200" />

      {/* bars */}
      <div className="mt-4 flex h-52 items-end gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="w-full rounded-t-lg bg-slate-200"
            style={{ height: `${30 + i * 10}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function HeatTileSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-20 rounded-xl border border-slate-200 bg-slate-100"
        />
      ))}
    </div>
  );
}


export default function GeographySection({
  mode,
  setMode,
  byState = [],
  byDistrict = [],
  state,
  setState,
  district,
  setDistrict,
  topStates = [],
  topDistricts = [],
  maxMapValue = 0,
  loading = false,
  isRetailerMobile = false,   // ✅ add this
}) {
  const barData = mode === "state" ? topStates : topDistricts;
  const tileData = mode === "state" ? byState : byDistrict;

  const hasBarData = Array.isArray(barData) && barData.length > 0;
  const hasTileData = Array.isArray(tileData) && tileData.length > 0;

  const BAR_COLORS = [
  "#2563EB", // blue
  "#16A34A", // green
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#14B8A6", // teal
  "#EC4899", // pink
];


  return (
    <Card
      title="Geography Analysis"
      className={
        isRetailerMobile
          ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
          : ""
      }
      right={
        <div className="flex gap-2 overflow-x-auto">
          <Chip
              active={mode === "state"}
              onClick={() => setMode("state")}
              isRetailerMobile={isRetailerMobile}
            >
              State Wise
            </Chip>

            <Chip
              active={mode === "district"}
              onClick={() => setMode("district")}
              isRetailerMobile={isRetailerMobile}
            >
              District Wise
            </Chip>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ================= BAR CHART ================= */}
        <div
            className={`h-80 rounded-2xl p-4 ${
              isRetailerMobile
                ? "bg-white/10 backdrop-blur-xl border border-white/30"
                : "border border-blue-100 bg-white"
            }`}
          >
          <p className="text-sm font-semibold text-slate-900">
            {mode === "state"
              ? "Top States by Revenue"
              : "Top Districts by Revenue"}
          </p>
          <p className="mt-1 text-xs font-semibold text-slate-500">
            Based on current filters
          </p>

          <div className="mt-3 h-64">
            {loading ? (
              <BarChartSkeleton/>
            ) : !hasBarData ? (
              <EmptyState text="No data found" />
            ) : (
             <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                barCategoryGap="40%"
                barGap={8}
              >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isRetailerMobile ? "rgba(255,255,255,0.2)" : "#e5e7eb"}
                  />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />

                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  radius={[6, 6, 0, 0]}
                  barSize={28}        // ✅ fixed width
                  maxBarSize={28}     // ✅ prevents stretching
                >
                  {barData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={BAR_COLORS[index % BAR_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ================= HEAT TILES ================= */}
        <div
          className={`rounded-2xl p-4 ${
            isRetailerMobile
              ? "bg-white/10 backdrop-blur-xl border border-white/30"
              : "border border-blue-100 bg-white"
          }`}
        >
          <p className={`text-sm font-semibold ${
            isRetailerMobile ? "text-slate-900" : "text-slate-900"
          }`}>
            {mode === "state"
              ? "State Heat Tiles (Revenue)"
              : "District Heat Tiles (Revenue)"}
          </p>
          <p className="mt-1 text-xs font-semibold text-slate-500">
            Click tile to quick-filter
          </p>

          <div className="mt-3 min-h-[12rem]">
            {loading ? (
              <HeatTileSkeleton/>
            ) : !hasTileData ? (
              <EmptyState text="No data found" />
            ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {tileData.slice(0, 9).map((x) => {
                  const label =
                    mode === "state"
                      ? x.name
                      : `${x.state || "—"} / ${x.name}`;

                  const active =
                    mode === "state"
                      ? state === x.name
                      : state === x.state && district === x.name;

                  return (
                    <HeatTile
                      key={x.key || x.name}
                      name={label}
                      value={x.revenue}
                      max={maxMapValue}
                      active={active}
                      onClick={() => {
                        if (mode === "state") {
                          setState(x.name);
                          setDistrict("ALL");
                        } else {
                          setState(x.state);
                          setDistrict(x.name);
                        }
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}


// import {
//   ResponsiveContainer,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   Legend,
//   PieChart,
//   Pie,
//   Cell,
//   LineChart,
//   Line,
//   CartesianGrid,
// } from "recharts";
// import Card from "../../../Component/RetailerComponents/Card";

// import { money } from "../../../utils/helpers";
// import HeatTile from "../../../Component/RetailerComponents/HeatTile";
// import { Chip } from "../../../Component/RetailerComponents/Chip";

// export default function GeographySection({
//   mode, setMode,
//   byState, byDistrict,
//   state, setState,
//   district, setDistrict,
//   topStates, topDistricts,
//   maxMapValue
// }) {

//     console.log("state: ", state)
//   return (
//      <Card
//         title="Geography Analysis"
//         right={
//             <div className="flex gap-2 overflow-x-auto">
//             <Chip active={mode === "state"} onClick={() => setMode("state")}>State Wise</Chip>
//             <Chip active={mode === "district"} onClick={() => setMode("district")}>District Wise</Chip>
//             </div>
//         }
//         >
//         <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
//             {/* Bars */}
//             <div className="h-80 rounded-2xl border border-blue-100 bg-white p-4">
//             <p className="text-sm font-semibold text-slate-900">
//                 {mode === "state" ? "Top States by Revenue" : "Top Districts by Revenue"}
//             </p>
//             <p className="mt-1 text-xs font-semibold text-slate-500">Based on current filters</p>

//             <div className="mt-3 h-64">
//                 <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={mode === "state" ? topStates : topDistricts}>
//                     <CartesianGrid strokeDasharray="3 3" />
//                     <XAxis dataKey={mode === "state" ? "name" : "name"} />
//                     <YAxis />
//                     <Tooltip formatter={(v) => money(v)} />
//                     <Legend />
//                     <Bar dataKey="revenue" name="Revenue" />
//                 </BarChart>
//                 </ResponsiveContainer>
//             </div>
//             </div>

//             {/* “Map-like tiles” */}
//             <div className="rounded-2xl border border-blue-100 bg-white p-4">
//             <p className="text-sm font-semibold text-slate-900">
//                 {mode === "state" ? "State Heat Tiles (Revenue)" : "District Heat Tiles (Revenue)"}
//             </p>
//             <p className="mt-1 text-xs font-semibold text-slate-500">
//                 Click tile to quick-filter (for district: sets State + District)
//             </p>

//             <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
//                 {(mode === "state" ? byState : byDistrict.slice(0, 9)).map((x) => {
//                 const label =
//   mode === "state"
//     ? x.name
//     : `${state !== "ALL" ? state : "—"} / ${x.name}`;

//                 const active =
//                     mode === "state"
//                     ? state === x.name
//                     : (state === x.state && district === x.name);

//                 return (
//                     <HeatTile
//                     key={mode === "state" ? x.name : x.key}
//                     name={label}
//                     value={x.revenue}
//                     max={maxMapValue}
//                     active={active}
//                     onClick={() => {
//                         if (mode === "state") {
//                         setState(x.name); 
//                         setState(x.state);
//                         setDistrict(x.name);
//                         }
//                     }}
//                     />
//                 );
//                 })}
//             </div>
//             </div>
//         </div>
//         </Card>
//   );
// }
